import { Component, signal, Inject, Optional } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { environment } from '../../../../../../apps/gs-2.0/src/environments/environment';

export interface DonorLoginData {
  errorMessage?: string;
  errorHtml?: string; // For HTML error messages with clickable links
  onLogin?: (username: string, password: string) => Promise<void>;
  submitLabel?: string;
  submitLoadingLabel?: string;
}

@Component({
  selector: 'app-donor-login',
  standalone: false,
  templateUrl: './donor-login.component.html',
  styleUrl: './donor-login.component.scss',
})
export class DonorLoginComponent {
  username = '';
  password = '';
  showPassword = signal<boolean>(false);
  isLoading = signal<boolean>(false);
  errorMessage = signal<string>('');
  errorHtml = signal<string>('');

  submitLabel: string;
  submitLoadingLabel: string;

  // Get URLs from environment
  termsUrl = environment.fund.terms;
  privacyPolicyUrl = environment.fund.privacyPolicy;
  contactUrl = environment.fund.contact;

  constructor(
    private dialogRef: MatDialogRef<DonorLoginComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data?: DonorLoginData
  ) {
    this.submitLabel = data?.submitLabel ?? 'auth.donor_login.submit';
    this.submitLoadingLabel = data?.submitLoadingLabel ?? 'auth.donor_login.submitting';

    // Set initial error message if provided
    if (data?.errorHtml) {
      this.errorHtml.set(data.errorHtml);
    } else if (data?.errorMessage) {
      this.errorMessage.set(data.errorMessage);
    }
  }

  /**
   * Clears the error message when user starts typing
   */
  clearError(): void {
    if (this.errorMessage()) {
      this.errorMessage.set('');
    }
    if (this.errorHtml()) {
      this.errorHtml.set('');
    }
  }

  /**
   * Handles form submission for login
   */
  async onSubmitLogin(): Promise<void> {
    if (!this.username || !this.password) {
      this.errorMessage.set('Please enter both username and password');
      return;
    }

    this.errorMessage.set('');
    this.errorHtml.set('');
    this.isLoading.set(true);

    // If parent provided onLogin callback, use it
    if (this.data?.onLogin) {
      try {
        await this.data.onLogin(this.username, this.password);
        // Success - modal will be closed by parent
      } catch (error: any) {
        // Error - show message and reset loading
        const errorMsg = error?.message || 'Login failed. Please try again.';
        // Check if error message contains HTML (simple check for <a> tag)
        if (errorMsg.includes('<a')) {
          this.errorHtml.set(errorMsg);
        } else {
          this.errorMessage.set(errorMsg);
        }
        this.isLoading.set(false);
      }
    } else {
      // Fallback: close dialog and return credentials for authentication
      this.dialogRef.close({
        action: 'login',
        credentials: {
          username: this.username,
          password: this.password
        }
      });
    }
  }

  /**
   * Public method to set error message and reset loading state
   * Called by parent component when login fails
   */
  showError(message: string): void {
    // Check if message contains HTML
    if (message.includes('<a')) {
      this.errorHtml.set(message);
      this.errorMessage.set('');
    } else {
      this.errorMessage.set(message);
      this.errorHtml.set('');
    }
    this.isLoading.set(false);
  }

  /**
   * Toggles password visibility
   */
  togglePasswordVisibility(): void {
    this.showPassword.update(value => !value);
  }

  /**
   * Handles forgot password click
   * Redirects directly to Keycloak's forgot password page
   */
  onForgotPassword(event: Event): void {
    event.preventDefault();

    // Close the donor-login dialog and trigger redirect to Keycloak
    this.dialogRef.close({
      action: 'forgot-password'
    });
  }

  /**
   * Handles continue as guest button click
   */
  onContinueAsGuest(): void {
    this.dialogRef.close({ action: 'guest' });
  }

  /**
   * Handles close button click
   */
  onClose(): void {
    this.dialogRef.close({ action: 'close' });
  }
}
